import { createUseStyles } from '@/services/createUseStyles';
import { PER_PAGE_DEFAULT } from '@/utils/queryParameters';
import { Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { DataGridPro, DataGridProProps, GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import { DataGridPagination } from './DataGridPagination';
import { UnsortedColumnIcon } from './UnsortedColumnIcon';

const useStyles = createUseStyles((theme: Theme) => ({
  DataGrid_container: {
    height: '100%',
    width: '100%',
  },
  DataGrid_grid: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 0,
    fontSize: 12,
    height: '100%',
    '& .MuiDataGrid-columnHeaders': {
      background: theme.palette.background.default,
      borderBottom: `1px solid ${theme.palette.divider}`,
      '& .MuiDataGrid-columnHeader': {
        outline: 'none',
        '&:focus, &:focus-within': {
          outline: 'none',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          color: theme.palette.text.primary,
        },
      },
    },
    '& .MuiDataGrid-columnHeaders .MuiDataGrid-filler': {
      background: theme.palette.background.default,
    },
    '& .MuiDataGrid-row.Mui-selected': {
      background: theme.palette.info.lightest,
      '&:hover': {
        background: theme.palette.info.hover,
      },
    },
    '& .MuiDataGrid-pinnedColumnHeaders:nth-of-type(1)': {
      boxShadow: 'none',
    },
    '& .MuiDataGrid-cell': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
      '& .MuiDataGrid-cellContent': {
        fontSize: '0.75rem',
      },
    },
    '& .MuiDataGrid-toolbarContainer': {
      position: 'absolute',
      bottom: '0.5rem',
      left: theme.spacing(0.75),
    },
    '& .MuiDataGrid-footerContainer': {
      justifyContent: 'flex-start',
      minHeight: 52,
      borderTop: `1px solid ${theme.palette.divider}`,
      '& .MuiTablePagination-root': {
        color: theme.palette.text.secondary,
        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
          fontSize: '0.75rem',
        },
        '& .MuiTablePagination-select': {
          fontSize: '0.75rem',
        },
      },
    },
  },
}));

interface DataGridProperties<T extends GridValidRowModel> extends DataGridProProps<T> {
  columns: GridColDef[];
  rows: T[];
  rowCount: number;
  onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
  paginationModel?: { page: number; pageSize: number };
  pageSizeOptions?: number[];
  getRowClassName?: (parameters: { row: T }) => string;
  onRowClick?: (parameters: { row: T }) => void;
  loading?: boolean;
}

export const DataGrid = <T extends { id: string | number }>({
  columns,
  rows,
  rowCount,
  onPaginationModelChange,
  paginationModel = { page: 0, pageSize: PER_PAGE_DEFAULT },
  pageSizeOptions = [PER_PAGE_DEFAULT],
  getRowClassName,
  onRowClick,
  loading = false,
  slots,
  ...properties
}: DataGridProperties<T>) => {
  const c = useStyles();

  return (
    <Box className={c.DataGrid_container}>
      <DataGridPro
        className={c.DataGrid_grid}
        rows={rows}
        columns={columns}
        slots={{ pagination: DataGridPagination, columnUnsortedIcon: UnsortedColumnIcon, ...slots }}
        rowCount={rowCount}
        pagination
        paginationMode='server'
        paginationModel={paginationModel}
        onPaginationModelChange={({ page, pageSize }) => onPaginationModelChange?.({ page, pageSize })}
        pageSizeOptions={pageSizeOptions}
        disableRowSelectionOnClick
        disableColumnFilter
        disableVirtualization
        getRowClassName={getRowClassName}
        onRowClick={onRowClick}
        loading={loading}
        columnHeaderHeight={40}
        rowHeight={52}
        hideFooterSelectedRowCount
        slotProps={{
          basePopper: {
            placement: 'bottom-start',
          },
        }}
        {...properties}
      />
    </Box>
  );
};
